class MediaToggle extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.button = this.querySelector('button');
    this.pausedByUser = false;
    this.button.addEventListener('click', (event) => {
      event.stopPropagation();
      const playing = this.button.dataset.paused === 'true';
      this.pausedByUser = !playing;
      this.setPlaying(playing);
    }, options);
    if (this.hasAttribute('data-announcement')) {
      this.addEventListener('carousel-ready', () => {
        this.button.hidden = false;
        this.render(!this.parentElement.swiper.autoplay.running);
      }, options);
      return;
    }
    this.mediaObserver = new MutationObserver(() => this.bindMedia());
    this.mediaObserver.observe(this.parentElement, { childList: true });
    this.bindMedia();
    if (this.hasAttribute('data-pdp')) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting && entry.intersectionRatio > 0.25;
        this.setPlaying(this.visible && !this.pausedByUser);
      }, { threshold: [0, 0.25] });
      this.observer.observe(this.parentElement);
    }
  }
  bindMedia() {
    const media = this.parentElement.querySelector('video, iframe');
    if (media === this.media) return;
    this.mediaController?.abort();
    this.media = media;
    this.button.hidden = !media;
    if (!media) return;
    this.mediaController = new AbortController();
    const options = { signal: this.mediaController.signal };
    this.button.hidden = false;
    if (this.media.tagName === 'VIDEO') {
      this.media.addEventListener('play', () => {
        if (this.pausedByUser || (this.hasAttribute('data-pdp') && this.visible === false)) this.media.pause();
        else {
          this.render(false);
          const overlay = this.parentElement.querySelector('.video-play-button');
          if (overlay) overlay.hidden = true;
        }
      }, options);
      this.media.addEventListener('pause', () => this.render(true), options);
      this.render(this.media.paused);
    } else {
      this.media.addEventListener('load', () => this.setPlaying(!this.pausedByUser && this.visible !== false), options);
      this.render(false);
    }
    if (this.pausedByUser || this.visible === false) this.setPlaying(false);
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.mediaController?.abort();
    this.mediaObserver?.disconnect();
    this.observer?.disconnect();
  }
  setPlaying(playing) {
    if (this.hasAttribute('data-announcement')) {
      const autoplay = this.parentElement.swiper?.autoplay;
      if (!autoplay) return;
      playing ? autoplay.start() : autoplay.stop();
      this.render(!playing);
      return;
    }
    if (!this.media) return;
    if (this.media.tagName === 'VIDEO') {
      if (playing) this.media.play().catch(() => this.render(true));
      else this.media.pause();
    } else {
      const source = this.media.getAttribute('src');
      if (!source) return;
      const origin = new URL(source, location.href).origin;
      const command = this.media.classList.contains('js-youtube')
        ? { event: 'command', func: playing ? 'playVideo' : 'pauseVideo', args: [] }
        : { method: playing ? 'play' : 'pause' };
      this.media.contentWindow.postMessage(JSON.stringify(command), origin);
      this.render(!playing);
    }
  }
  render(paused) {
    this.button.dataset.paused = String(paused);
    this.button.setAttribute('aria-label', paused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel);
    this.button.querySelector('[data-play-icon]').toggleAttribute('hidden', !paused);
    this.button.querySelector('[data-pause-icon]').toggleAttribute('hidden', paused);
  }
}
if (!customElements.get('media-toggle')) customElements.define('media-toggle', MediaToggle);
